import { useEffect, useRef, useState, useCallback } from 'react'

/**
 * useParallax - Hook for scroll-based parallax transformations
 * @param {Object} options
 * @param {number} options.speed - Parallax speed multiplier (default: 0.5)
 * @param {string} options.direction - 'vertical' or 'horizontal' (default: 'vertical')
 * @returns {Object} - { ref, offset, progress }
 */
export const useParallax = (options = {}) => {
  const { speed = 0.5, direction = 'vertical' } = options
  const ref = useRef(null)
  const [offset, setOffset] = useState(0)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return

      const rect = ref.current.getBoundingClientRect()
      const windowHeight = window.innerHeight

      // Calculate how far through the viewport the element is
      const elementProgress = 1 - (rect.top + rect.height) / (windowHeight + rect.height)
      const clampedProgress = Math.max(0, Math.min(1, elementProgress))

      setProgress(clampedProgress)
      setOffset((clampedProgress - 0.5) * speed * 100)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [speed])

  const style = direction === 'vertical'
    ? { transform: `translateY(${offset}px)` }
    : { transform: `translateX(${offset}px)` }

  return { ref, offset, progress, style }
}

/**
 * useScrollProgress - Track scroll progress within a section
 * @returns {Object} - { ref, progress (0-1), isInView }
 */
export const useScrollProgress = () => {
  const ref = useRef(null)
  const [progress, setProgress] = useState(0)
  const [isInView, setIsInView] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return

      const rect = ref.current.getBoundingClientRect()
      const windowHeight = window.innerHeight

      // Check if in view
      const inView = rect.top < windowHeight && rect.bottom > 0
      setIsInView(inView)

      if (inView) {
        // Progress from 0 (just entering) to 1 (just leaving)
        const elementProgress = 1 - rect.top / windowHeight
        setProgress(Math.max(0, Math.min(1, elementProgress)))
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return { ref, progress, isInView }
}

/**
 * useMouseParallax - Parallax effect based on mouse position
 * @param {Object} options
 * @param {number} options.intensity - Movement intensity (default: 20)
 * @returns {Object} - { ref, x, y, style }
 */
export const useMouseParallax = (options = {}) => {
  const { intensity = 20 } = options
  const ref = useRef(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!ref.current) return

      const rect = ref.current.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2

      const x = ((e.clientX - centerX) / rect.width) * intensity
      const y = ((e.clientY - centerY) / rect.height) * intensity

      setPosition({ x, y })
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true })

    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [intensity])

  return {
    ref,
    x: position.x,
    y: position.y,
    style: { transform: `translate(${position.x}px, ${position.y}px)` }
  }
}

/**
 * useTilt - 3D tilt effect on hover
 * @param {Object} options
 * @param {number} options.maxTilt - Maximum tilt angle in degrees (default: 15)
 * @param {number} options.scale - Scale on hover (default: 1.02)
 * @param {number} options.perspective - Perspective value (default: 1000)
 * @returns {Object} - { ref, style, onMouseMove, onMouseLeave }
 */
export const useTilt = (options = {}) => {
  const { maxTilt = 15, scale = 1.02, perspective = 1000 } = options
  const ref = useRef(null)
  const [tilt, setTilt] = useState({ x: 0, y: 0, isHovering: false })

  const handleMouseMove = useCallback((e) => {
    if (!ref.current) return

    const rect = ref.current.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height

    const tiltX = (y - 0.5) * maxTilt * 2
    const tiltY = (x - 0.5) * -maxTilt * 2

    setTilt({ x: tiltX, y: tiltY, isHovering: true })
  }, [maxTilt])

  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0, isHovering: false })
  }, [])

  const style = {
    transform: tilt.isHovering
      ? `perspective(${perspective}px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${scale})`
      : 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)',
    transition: tilt.isHovering ? 'transform 0.1s ease-out' : 'transform 0.5s ease-out',
  }

  return {
    ref,
    style,
    handlers: {
      onMouseMove: handleMouseMove,
      onMouseLeave: handleMouseLeave,
    }
  }
}

/**
 * useCountUp - Animated counter effect
 * @param {number} end - Target number
 * @param {Object} options
 * @param {number} options.duration - Animation duration in ms (default: 2000)
 * @param {boolean} options.startOnView - Start when in view (default: true)
 * @returns {Object} - { ref, count, isComplete }
 */
export const useCountUp = (end, options = {}) => {
  const { duration = 2000, startOnView = true } = options
  const ref = useRef(null)
  const [count, setCount] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const [hasStarted, setHasStarted] = useState(false)

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true)
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [startOnView, hasStarted])

  useEffect(() => {
    if (!hasStarted) return

    const startTime = Date.now()
    const startValue = 0

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Easing function (ease-out cubic)
      const eased = 1 - Math.pow(1 - progress, 3)

      const currentCount = Math.round(startValue + (end - startValue) * eased)
      setCount(currentCount)

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        setIsComplete(true)
      }
    }

    requestAnimationFrame(animate)
  }, [end, duration, hasStarted])

  return { ref, count, isComplete }
}
